import React from 'react';
import { DropdownMenuActions, DropdownMenuActionType } from 'framework/ui/menu';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import ReplyAllIcon from '@mui/icons-material/ReplyAll';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import { useArtifactDownloadHelper } from 'domain/shared/artifact';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { DEFAULT_ERROR_MESSAGE, FormFieldsData } from 'framework/form';
import {
  DOWNLOAD_PROCESS_ASSETS,
  PROCESS_ASSETS_CTO,
  PROCESS_ASSETS_STATUS,
  UPDATE_PROCESS_ASSETS,
} from 'domain/gpo/processAssets/constants';
import { ProcessAssetsType } from 'domain/gpo/processAssets/types';
import ViewProcessAssetAction from './viewButton/ViewProcessAssetAction';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { useAsyncValue } from 'framework/utils';
import { useAuthorization } from 'framework/authorization';
import { useListContext } from 'framework/list';
import { useShowContext } from 'framework/show';
import EditProcessAssetDocumentAction from './EditProcessAssetDocumentAction';

interface PolicyDocumentListActionsProps {
  isPrevious?: boolean;
}

const ProcessAssetListActions: React.FC<PolicyDocumentListActionsProps> = ({ isPrevious = false }) => {
  const { isGranted } = useAuthorization();
  const { getUserAlias } = useAuthenticatedUserService();
  const { addMessage } = useMessageService();
  const { downloadDocumentStorage } = useArtifactDownloadHelper();
  const { reloadData, updateInList } = useListContext();
  const { data } = useShowContext<ProcessAssetsType>();

  const currentUserAlias = useAsyncValue(async () => getUserAlias());

  const documentData = data.getData();
  const { metadata } = documentData;

  const accessPublishActions = isGranted([PROCESS_ASSETS_CTO]) || metadata.owner === currentUserAlias;

  const handleDownload = async (filePath: string) => {
    try {
      await downloadDocumentStorage(filePath);
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const handleUpdateList = (updatedEntity: FormFieldsData) => {
    const updateData = updatedEntity as ProcessAssetsType;

    updateInList!({
      ...documentData,
      path: updateData.path || documentData.path,
      id: documentData.id,
      metadata: {
        ...metadata,
        status: updateData.status,
      },
    } as any);
  };

  const reloadDataList = () => {
    reloadData!();
  };

  const createViewAction = (
    Icon: React.ComponentType,
    title: string,
    isVisible: boolean,
    permission: string | string[],
  ) => ({
    isVisible,
    permission,
    form: {
      Component: ViewProcessAssetAction,
      title: 'Template',
      afterSubmit: {
        execute: handleUpdateList,
      },
    },
    icon: <Icon />,
    title,
  });

  const getActions = (): DropdownMenuActionType[] => [
    createViewAction(
      PublishIcon,
      'Publish',
      metadata.status === PROCESS_ASSETS_STATUS.UNPUBLISHED && !isPrevious && accessPublishActions,
      UPDATE_PROCESS_ASSETS,
    ),
    createViewAction(
      ReplyAllIcon,
      'Unpublish',
      metadata.status === PROCESS_ASSETS_STATUS.PUBLISHED && !isPrevious && accessPublishActions,
      UPDATE_PROCESS_ASSETS,
    ),
    createViewAction(VisibilityIcon, 'View', !isPrevious, UPDATE_PROCESS_ASSETS),
    {
      permission: UPDATE_PROCESS_ASSETS,
      isVisible: !isPrevious,
      form: {
        Component: EditProcessAssetDocumentAction,
        title: 'Edit template',
        afterSubmit: {
          execute: reloadDataList,
        },
      },
      icon: <EditIcon />,
      title: 'Edit',
    },
    {
      permission: DOWNLOAD_PROCESS_ASSETS,
      onClick: entityData => handleDownload(entityData?.path),
      icon: <DownloadIcon />,
      title: 'Download',
    },
  ];

  return (
    <>
      <DropdownMenuActions actions={getActions()} />
    </>
  );
};

export default ProcessAssetListActions;
